import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/es";
import type { Language as ElementPlusLanguage } from "element-plus/es/locale";
import elementPlusDe from "element-plus/es/locale/lang/de";
import elementPlusEn from "element-plus/es/locale/lang/en";
import elementPlusEs from "element-plus/es/locale/lang/es";
import { computed, ref, watch } from "vue";
import {
  createI18n,
  type I18nOptions,
  type LocaleMessageDictionary,
} from "vue-i18n";

export type LanguageCode = "de" | "en" | "es";

type LanguageDefinition = {
  elementPlusLocale: ElementPlusLanguage;
  locale: string;
  label: string;
};

type LanguageI18nOptions = I18nOptions<
  {
    message: Record<string, string>;
    datetime: Record<string, never>;
    number: Record<string, never>;
  },
  LanguageCode
>;

type InitLanguagesOptions = {
  languages: Set<LanguageCode>;
  defaultLanguage: LanguageCode;
  messages: Partial<
    Record<LanguageCode, LocaleMessageDictionary<Record<string, string>>>
  >;
};

const languageDefinitions: Record<LanguageCode, LanguageDefinition> = {
  de: {
    elementPlusLocale: elementPlusDe,
    locale: "de-DE",
    label: "german",
  },
  en: {
    elementPlusLocale: elementPlusEn,
    locale: "en-US",
    label: "english",
  },
  es: {
    elementPlusLocale: elementPlusEs,
    locale: "es-ES",
    label: "spanish",
  },
};

const configuredLanguages = ref<Set<LanguageCode>>(new Set(["de"]));
const configuredDefaultLanguage = ref<LanguageCode>("de");
const currentLanguage = ref<LanguageCode>("de");

export const applicationLanguage = computed(() => currentLanguage.value);

export const elementPlusLocale = computed(
  () => getLanguageDefinition(currentLanguage.value).elementPlusLocale,
);

export const languageLocale = computed(
  () => getLanguageDefinition(currentLanguage.value).locale,
);

export function getConfiguredLanguages() {
  return [...configuredLanguages.value];
}

export function getDefaultLanguage() {
  return configuredDefaultLanguage.value;
}

export function getLanguageLabel(language: LanguageCode) {
  return getLanguageDefinition(language).label;
}

function isLanguageCode(language: string): language is LanguageCode {
  return language === "de" || language === "en" || language === "es";
}

function getLanguageDefinition(language: LanguageCode) {
  switch (language) {
    case "de":
      return languageDefinitions.de;
    case "en":
      return languageDefinitions.en;
    case "es":
      return languageDefinitions.es;
  }
}

export function isConfiguredLanguage(
  language: string,
): language is LanguageCode {
  return isLanguageCode(language) && configuredLanguages.value.has(language);
}

export function setLanguage(language: string) {
  if (!isConfiguredLanguage(language)) {
    throw new Error(`Unsupported language: ${language}`);
  }

  currentLanguage.value = language;
}

export function initLanguages({
  languages,
  defaultLanguage,
  messages,
}: InitLanguagesOptions) {
  if (!languages.has(defaultLanguage)) {
    throw new Error("Default language must be configured");
  }

  configuredLanguages.value = new Set(languages);
  configuredDefaultLanguage.value = defaultLanguage;
  currentLanguage.value = defaultLanguage;

  const i18n = createI18n<false, LanguageI18nOptions>({
    legacy: false,
    locale: defaultLanguage,
    fallbackLocale: languages.has("en") ? "en" : defaultLanguage,
    messages: {
      de: languages.has("de") ? (messages.de ?? {}) : {},
      en: languages.has("en") ? (messages.en ?? {}) : {},
      es: languages.has("es") ? (messages.es ?? {}) : {},
    },
  });

  dayjs.locale(defaultLanguage);

  watch(
    currentLanguage,
    (language) => {
      dayjs.locale(language);
      i18n.global.locale.value = language;
    },
    { flush: "sync" },
  );

  return i18n;
}
